import type { FC } from "react";
import { Card, CardRowSimple, type GridCard, LoadingSpinner } from "widgets";

import { pluralizeWord } from "@/app-utils/string.ts";
import type { Organism } from "@/graphql.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";
import { GenomeTile } from "@/ui-modules/core/tiles";
import { useFetchVirtualGenomesStatistics } from "@/ui-modules/entities/genomes/graphql/useFetchGenomeStatistics.ts";

export interface IVirtualGenomesCardProps extends GridCard {
  genomeId: Organism["id"];
}

type GroupedGenomes = Record<string, Organism[]>;

const VirtualGenomesCard: FC<IVirtualGenomesCardProps> = (props) => {
  const { data, loading, error } = useFetchVirtualGenomesStatistics({ genomeId: props.genomeId });

  const virtualGenomes = data?.virtualGenomes;
  const components = data?.components;

  if (virtualGenomes?.length === 0 && components?.length === 0) {
    return null;
  }

  const groupedVirtualGenomes = virtualGenomes?.reduce<GroupedGenomes>((acc, item) => {
    (acc[item.sequencingType ?? "Restricted Genome"] = acc[item.sequencingType ?? "Restricted Genome"] || []).push(
      item as Organism,
    );
    return acc;
  }, {});

  return (
    <Card
      shrinkable={(virtualGenomes && virtualGenomes?.length > 7) || (components && components?.length > 7)}
      title={"Genome Relationships"}
      tag={{ label: String(virtualGenomes?.length || components?.length) }}
    >
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        // Fallback. Usually graphql errors are handled globally
        <ContentPlaceholder>{error.message}</ContentPlaceholder>
      ) : (
        <>
          {components && components?.length > 0 ? (
            <CardRowSimple label={"Components"}>
              {components?.map((component) => <GenomeTile key={component.id} genome={component as Organism} />)}
            </CardRowSimple>
          ) : null}
          {groupedVirtualGenomes &&
            Object.keys(groupedVirtualGenomes).map((key) => {
              const genomesInGroup = groupedVirtualGenomes?.[key];
              return (
                <CardRowSimple label={pluralizeWord(key, genomesInGroup?.length)} key={key}>
                  {genomesInGroup.map((virtualGenome) => {
                    if (virtualGenome) {
                      return <GenomeTile key={virtualGenome.id} genome={virtualGenome as Organism} />;
                    }
                    return null;
                  })}
                </CardRowSimple>
              );
            })}
        </>
      )}
    </Card>
  );
};

export { VirtualGenomesCard };
